import { FC, useMemo, useState } from 'react';
import { Spin } from 'antd';
import type { MenuProps } from 'antd';
import { TbCopy, TbFilePlus } from 'react-icons/tb';

import type { SourceQuotePendingAction } from '@/hooks/useSourceQuoteActions';
import DropdownPanel, { DropdownPanelMenu } from '@/shared/components/dropdowns/DropdownPanel';
import LearnMoreFooter from '@/shared/components/layouts/footers/LearnMoreFooter';
import type { Memo } from '@/types';

interface QuoteMemoDropdownPanelProps {
    active?: boolean;
    memos: Memo[];
    loadingMemos?: boolean;
    pendingAction?: SourceQuotePendingAction;
    onCopy: () => void | Promise<void>;
    onCreate: () => void | Promise<void>;
    onSelectMemo: (memo: Memo) => void | Promise<void>;
}

const QuoteMemoDropdownPanel: FC<QuoteMemoDropdownPanelProps> = ({
    active = true,
    memos,
    loadingMemos = false,
    pendingAction,
    onCopy,
    onCreate,
    onSelectMemo,
}) => {
    const [query, setQuery] = useState('');
    const busy = Boolean(pendingAction);
    const normalizedQuery = query.trim().toLocaleLowerCase();
    const filteredMemos = useMemo(
        () =>
            memos.filter(
                (memo) =>
                    !memo.archived &&
                    (!normalizedQuery || (memo.title || 'Untitled Memo').toLocaleLowerCase().includes(normalizedQuery)),
            ),
        [memos, normalizedQuery],
    );
    const memoItems = useMemo<MenuProps['items']>(
        () =>
            filteredMemos.length
                ? filteredMemos.map((memo) => ({
                      disabled: !active || busy || loadingMemos,
                      icon:
                          pendingAction?.type === 'add-to-memo' && pendingAction.memoId === memo.id ? (
                              <Spin size='small' />
                          ) : undefined,
                      key: `memo:${memo.id}`,
                      label: `${pendingAction?.type === 'add-to-memo' && pendingAction.memoId === memo.id ? 'Adding to ' : ''}${memo.title || 'Untitled Memo'}`,
                      onClick: () => onSelectMemo(memo),
                  }))
                : [
                      {
                          disabled: true,
                          key: 'no-memos',
                          label: normalizedQuery ? 'No matching Memos' : 'No existing Memos',
                      },
                  ],
        [active, busy, filteredMemos, loadingMemos, normalizedQuery, onSelectMemo, pendingAction],
    );
    const actionItems = useMemo<MenuProps['items']>(
        () => [
            {
                disabled: !active || busy,
                icon: pendingAction?.type === 'create-memo' ? <Spin size='small' /> : <TbFilePlus size='1rem' />,
                key: 'new-memo',
                label: pendingAction?.type === 'create-memo' ? 'Creating Memo…' : 'Create New Memo',
                onClick: onCreate,
            },
            {
                disabled: !active || busy,
                icon: pendingAction?.type === 'copy-quote' ? <Spin size='small' /> : <TbCopy size='1rem' />,
                key: 'copy-quote',
                label: pendingAction?.type === 'copy-quote' ? 'Copying Quote…' : 'Copy Quote and Source',
                onClick: onCopy,
            },
        ],
        [active, busy, onCopy, onCreate, pendingAction],
    );

    return (
        <DropdownPanel
            actions={<DropdownPanelMenu items={actionItems} />}
            footer={<LearnMoreFooter topic='Memos and source quotes' variant='dropdown' />}
            maxHeight='min(28rem, 70vh)'
            search={{
                ariaLabel: 'Search Memos',
                placeholder: 'Search Memos...',
                setValue: setQuery,
                value: query,
            }}
            width='20rem'
        >
            {loadingMemos ? (
                <div role='status' style={{ padding: '1rem', textAlign: 'center' }}>
                    <Spin size='small' /> <small>Loading Memos…</small>
                </div>
            ) : (
                <DropdownPanelMenu items={memoItems} />
            )}
        </DropdownPanel>
    );
};

export default QuoteMemoDropdownPanel;
